import {
  API_USER_PATIENT_PROFILE_WEEKLY_REPORTS,
  AppUserPatientProfileWeeklyReport,
  UserPatientProfileWeeklyReportItem,
  UserPatientProfileWeeklyReportInput,
  UserPatientProfileWeeklyReportShort,
  UserPatientProfileWeeklyReportPatch,
  UserPatientProfileWeeklyReportItemForReview,
  ReviewInput
} from './models';
import { apiRtk } from 'utils/rtk-query';
import { DynamicService } from 'utils/service';
import { SELECT_WEEKLY_REPORT_ITEM, SELECT_WEEKLY_REPORT_ITEM_FOR_REVIEW_MAINTENANCE, SELECT_WEEKLY_REPORT_ITEM_FOR_REVIEW_SUBSCRIPTIONS, SELECT_WEEKLY_REPORT_ITEM_FOR_REVIEW_NEW_SUBSCRIPTIONS } from './queries';
import * as dynamic from 'utils/dynamic-helpers';
import { AxiosError } from 'axios';
import { ServiceUserPatientProfileWeeklyReportActivities } from 'services/user-patient-profile-weekly-report-activities';
import { promiseChunks } from 'utils/promises';

class Service extends DynamicService<AppUserPatientProfileWeeklyReport> {
  getWeeklyReports = async (input: UserPatientProfileWeeklyReportInput) => {
    const { data } = await this.getAllDynamic<UserPatientProfileWeeklyReportItem>({
      select: SELECT_WEEKLY_REPORT_ITEM,
      filter: dynamic
        .mergeFilters(
          `done=${input.done}`,
          dynamic.createFilterEquals('reportDate.year', input.year),
        )
        .join('&&'),
        orderBy: 'reportDate desc',
    });

    return data.value;
  };
  private reviewActivities = async (activityIDs: string[], userEmployeeProfileID: string) => {
    const executors = activityIDs.map((activityID) => {
      return () => {
        return ServiceUserPatientProfileWeeklyReportActivities.patch({ id: activityID, reviewed: true, userEmployeeProfileID });
      }
    });

    await promiseChunks(executors, 10);

    return { completed: activityIDs.length };
  }
  reviewSubscriptions = async (input: ReviewInput) => {
    const { data } = await this.getDynamic<UserPatientProfileWeeklyReportItemForReview>(input.id, {
      select: SELECT_WEEKLY_REPORT_ITEM_FOR_REVIEW_SUBSCRIPTIONS
    });

    const activityIDs = data.activityIDs;

    return this.reviewActivities(activityIDs, input.userEmployeeProfileID);
  };
  reviewNewSubscriptions = async (input: ReviewInput) => {
    const { data } = await this.getDynamic<UserPatientProfileWeeklyReportItemForReview>(input.id, {
      select: SELECT_WEEKLY_REPORT_ITEM_FOR_REVIEW_NEW_SUBSCRIPTIONS
    });

    const activityIDs = data.activityIDs;

    return this.reviewActivities(activityIDs, input.userEmployeeProfileID);
  };
  reviewMaintenance = async (input: ReviewInput) => {
    const { data } = await this.getDynamic<UserPatientProfileWeeklyReportItemForReview>(input.id, {
      select: SELECT_WEEKLY_REPORT_ITEM_FOR_REVIEW_MAINTENANCE
    });

    const activityIDs = data.activityIDs;

    return this.reviewActivities(activityIDs, input.userEmployeeProfileID);
  }
}

export const ServiceUserPatientProfileWeeklyReports = new Service({
  mainField: 'id',
  getAll: API_USER_PATIENT_PROFILE_WEEKLY_REPORTS.GET_ALL_DYNAMIC,
  post: API_USER_PATIENT_PROFILE_WEEKLY_REPORTS.POST,
  patch: API_USER_PATIENT_PROFILE_WEEKLY_REPORTS.PATCH,
  delete: API_USER_PATIENT_PROFILE_WEEKLY_REPORTS.DELETE,
});

export const apiUserPatientProfileWeeklyReports = apiRtk.injectEndpoints({
  endpoints: (builder) => ({
    getWeeklyReportActivities: builder.query<UserPatientProfileWeeklyReportShort, string>({
      queryFn: async (id) => {
        try {
          const { data } = await ServiceUserPatientProfileWeeklyReports.getDynamic<UserPatientProfileWeeklyReportShort>(
            id,
          );
          return { data };
        } catch (error) {
          return { error: error as AxiosError };
        }
      },
    }),
    patchWeeklyReport: builder.mutation<void, UserPatientProfileWeeklyReportPatch>({
      queryFn: async (input) => {
        try {
          await ServiceUserPatientProfileWeeklyReports.patch(input);
          return { data: undefined };
        } catch (error) {
          return { error: error as AxiosError };
        }
      },
    }),
    reviewSubscriptionsWeeklyReport: builder.mutation<void, ReviewInput>({
      queryFn: async (id) => {
        try {
          await ServiceUserPatientProfileWeeklyReports.reviewSubscriptions(id);
          return { data: undefined };
        } catch (error) {
          return { error: error as AxiosError };
        }
      },
    }),
    reviewNewSubscriptionsWeeklyReport: builder.mutation<void, ReviewInput>({
      queryFn: async (id) => {
        try {
          await ServiceUserPatientProfileWeeklyReports.reviewNewSubscriptions(id);
          return { data: undefined };
        } catch (error) {
          return { error: error as AxiosError };
        }
      },
    }),
    reviewMaintenanceWeeklyReport: builder.mutation<void, ReviewInput>({
      queryFn: async (id) => {
        try {
          await ServiceUserPatientProfileWeeklyReports.reviewMaintenance(id);
          return { data: undefined };
        } catch (error) {
          return { error: error as AxiosError };
        }
      },
    }),
  }),
});